import { Controller } from "@hotwired/stimulus"
import { readStripeAppearance, observeStripeTheme } from "../utils/stripe_appearance"
import { Modal } from "bootstrap"

const FULFILLED_STATUSES = ["fulfilled", "already_fulfilled"]

export default class extends Controller {
  static targets = ["payment", "errors", "submit", "status"]
  static values = {
    publishableKey: String,
    insurancePolicyId: String,
    customerEmail: String,
    paymentMethodTypes: String,
    stripeAccountId: String,
    billingCountry: String,
    lazy: { type: Boolean, default: false }
  }

  connect() {
    if (!this.publishableKeyValue) return

    this.unobserveTheme = observeStripeTheme(() => this.updateAppearance())

    if (this.lazyValue) {
      this.boundShow = this.onPanelShown.bind(this)
      document.addEventListener("payment-mode-selector:show", this.boundShow)
      document.addEventListener("ledger-billing-alternatives:show-one-shot", this.boundShow)
      return
    }

    this.boot()
  }

  disconnect() {
    document.removeEventListener("payment-mode-selector:show", this.boundShow)
    document.removeEventListener("ledger-billing-alternatives:show-one-shot", this.boundShow)
    if (this.paymentElement) this.paymentElement.destroy()
    if (this.unobserveTheme) this.unobserveTheme()
  }

  onPanelShown(event) {
    if (event.type === "payment-mode-selector:show" && event.detail?.mode !== "ledger_pay") return
    if (event.type === "ledger-billing-alternatives:show-one-shot" && !this.element.closest(".ledger-billing-one-shot-modal")) return
    if (event.type === "payment-mode-selector:show" && !this.element.closest('[data-mode="ledger_pay"]')) return
    if (this.booted) return

    this.boot()
  }

  boot() {
    this.booted = true
    this.waitForStripe()
  }

  waitForStripe() {
    if (typeof Stripe === "undefined") {
      setTimeout(() => this.waitForStripe(), 100)
      return
    }

    const options = this.stripeAccountIdValue
      ? { stripeAccount: this.stripeAccountIdValue }
      : {}
    this.stripe = Stripe(this.publishableKeyValue, options)
    this.initializePaymentIntent()
  }

  getCSRFToken() {
    const csrfMeta = document.querySelector("[name=\"csrf-token\"]")
    if (csrfMeta?.content) return csrfMeta.content
    const input = document.querySelector("input[name=\"authenticity_token\"]")
    return input?.value || ""
  }

  async initializePaymentIntent() {
    try {
      const response = await fetch(`/insurance_policies/${this.insurancePolicyIdValue}/create_payment_intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || "Impossible d'initialiser le paiement")
      }

      const data = await response.json()
      this.clientSecret = data.client_secret
      this.paymentIntentId = data.payment_intent_id
      this.setupElements()
    } catch (error) {
      this.displayError({ message: error.message })
    }
  }

  updateAppearance() {
    if (!this.elements) return
    this.elements.update({ appearance: readStripeAppearance() })
  }

  setupElements() {
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance: readStripeAppearance()
    })

    const paymentMethodTypes = JSON.parse(this.paymentMethodTypesValue || '["card"]')
    const billingCountry = this.billingCountryValue || "auto"

    this.paymentElement = this.elements.create("payment", {
      paymentMethodTypes,
      fields: {
        billingDetails: {
          email: "never",
          address: { country: billingCountry, postalCode: "auto" }
        }
      }
    })

    this.paymentElement.mount(this.paymentTarget)
    this.paymentElement.on("ready", () => this.toggleSubmit(true))
    this.paymentElement.on("change", ({ error }) => {
      this.displayError(error)
      this.toggleSubmit(!error)
    })
  }

  async submit(event) {
    event.preventDefault()
    this.setLoading(true)

    try {
      const { error, paymentIntent } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          payment_method_data: {
            billing_details: { email: this.customerEmailValue }
          }
        },
        redirect: "if_required"
      })

      if (error) {
        this.displayError(error)
        this.setLoading(false)
        return
      }

      if (paymentIntent?.status === "succeeded") {
        await this.fulfillPayment(paymentIntent.id)
        this.showStatus("Paiement effectué avec succès.")
        this.closeModalAndRefresh()
        return
      }

      this.displayError({ message: "Le paiement n'a pas abouti." })
      this.setLoading(false)
    } catch (error) {
      this.displayError({ message: error.message })
      this.setLoading(false)
    }
  }

  async fulfillPayment(paymentIntentId) {
    const response = await fetch(`/insurance_policies/${this.insurancePolicyIdValue}/fulfill_ledger_payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken()
      },
      body: JSON.stringify({
        payment_intent_id: paymentIntentId,
        global_workflow: new URL(window.location.href).searchParams.get("global_workflow")
      })
    })

    const data = await response.json().catch(() => ({}))
    if (!FULFILLED_STATUSES.includes(data.status)) {
      throw new Error(data.error || "Erreur lors de la confirmation du paiement")
    }

    return data
  }

  closeModalAndRefresh() {
    const modal = this.element.closest(".modal")
    if (!modal) {
      this.submitPaymentStepOrRefresh()
      return
    }

    let continued = false
    const continueOnce = () => {
      if (continued) return
      continued = true
      modal.removeEventListener("hidden.bs.modal", continueOnce)
      this.cleanupModalArtifacts()
      this.submitPaymentStepOrRefresh()
    }
    modal.addEventListener("hidden.bs.modal", continueOnce)

    const instance = Modal.getInstance(modal)
    if (instance) {
      instance.hide()
      setTimeout(continueOnce, 400)
      return
    }

    continueOnce()
  }

  submitPaymentStepOrRefresh() {
    const form = document.querySelector("form[data-ledger-billing-step-form]")
    if (form) {
      const submit = form.querySelector("[data-payment-methods-target='subscribeButton']")
      if (submit) submit.disabled = false
      form.requestSubmit()
      return
    }

    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
      return
    }

    window.location.reload()
  }

  cleanupModalArtifacts() {
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
    document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove())
  }

  displayError(error) {
    if (!this.hasErrorsTarget) return
    if (error) {
      this.errorsTarget.textContent = error.message
      this.errorsTarget.style.display = "block"
    } else {
      this.errorsTarget.textContent = ""
      this.errorsTarget.style.display = "none"
    }
  }

  showStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  toggleSubmit(enabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !enabled
  }

  setLoading(loading) {
    if (!this.hasSubmitTarget) return
    this.submitTarget.disabled = loading
    this.submitTarget.textContent = loading ? "Paiement en cours..." : this.submitTarget.dataset.originalText
  }
}
